import validatePagerAddress from 'utils/validatePagerAddress';
import { validatePhoneNumberRegex } from 'utils/validatePhoneNumberRegex';
import { CONTACT_CHANNELS, methodsForChannel } from 'utils/contactMethods';
import type { ContactMethodInput } from 'hooks/useUpdateUserProfileSettings';

/** One editable row. `optedOut` is displayed but never submitted. */
export type ContactMethodDraft = {
  channel: ContactChannel;
  value: string;
  label: string;
  optedIn: boolean;
  optedOut: boolean;
};

export type ContactMethodDrafts = Record<ContactChannel, ContactMethodDraft[]>;

export function emptyDrafts(): ContactMethodDrafts {
  return { email: [], sms: [], voice: [], pager: [] };
}

/** Turns what the server returned into editable rows, preserving send order. */
export function toDrafts(
  methods: ContactMethod[] | undefined
): ContactMethodDrafts {
  return CONTACT_CHANNELS.reduce((drafts, channel) => {
    drafts[channel] = methodsForChannel(methods, channel).map((method) => ({
      channel,
      value: method.value,
      label: method.label ?? '',
      optedIn: method.opted_in,
      optedOut: method.opted_out,
    }));
    return drafts;
  }, emptyDrafts());
}

/**
 * Flattens the per-channel lists into the request body. Row order within a
 * channel becomes `position` on the server, so the array order is meaningful.
 *
 * Blank rows are dropped rather than rejected: an empty row is someone who
 * pressed Add and changed their mind, not an error to correct.
 */
export function toContactMethodInputs(
  drafts: ContactMethodDrafts
): ContactMethodInput[] {
  return CONTACT_CHANNELS.flatMap((channel) =>
    drafts[channel]
      .filter((draft) => draft.value.trim().length > 0)
      .map((draft) => ({
        channel,
        value: draft.value.trim(),
        label: draft.label.trim() || null,
        optedIn: draft.optedIn,
      }))
  );
}

/** Mirrors the server's per-channel format check. */
export function validateContactValue(
  channel: ContactChannel,
  value: string
): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  switch (channel) {
    case 'email':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
        ? undefined
        : 'Invalid email address.';
    case 'pager':
      return validatePagerAddress(trimmed)
        ? undefined
        : 'Invalid pager address.';
    default:
      return validatePhoneNumberRegex(trimmed)
        ? undefined
        : 'Invalid phone number.';
  }
}

/** Every reason the form cannot be submitted, so the page can list them. */
export function draftErrors(drafts: ContactMethodDrafts): string[] {
  const errors = CONTACT_CHANNELS.flatMap((channel) =>
    drafts[channel]
      .map((draft) => validateContactValue(channel, draft.value))
      .filter((error): error is string => Boolean(error))
  );

  const duplicates = CONTACT_CHANNELS.filter((channel) => {
    const values = drafts[channel]
      .map((draft) => draft.value.trim().toLowerCase())
      .filter(Boolean);
    return new Set(values).size !== values.length;
  }).map((channel) => `The same ${channel} entry is listed more than once.`);

  return [...new Set([...errors, ...duplicates])];
}

/**
 * Whether the user is granting consent on a number that did not have it, which
 * is what triggers the welcome text — surfaced so the form can say so before
 * saving rather than after.
 */
export function grantsNewSmsConsent(
  drafts: ContactMethodDrafts,
  original: ContactMethod[] | undefined
): boolean {
  const known = new Set(
    methodsForChannel(original, 'sms')
      .filter((method) => method.opted_in || method.opted_out)
      .map((method) => method.value)
  );

  return drafts.sms.some(
    (draft) =>
      draft.optedIn && draft.value.trim() && !known.has(draft.value.trim())
  );
}
